<template>
    <drawer title="Column Layout">
        <div class="layout-drawer">
            <section class="layout-drawer__row">
                <div class="layout-drawer__column">
                    <h4 class="layout-drawer__label">
                        Number of Columns
                    </h4>

                    <button
                        v-for="column in columnList"
                        :key="column"
                        class="layout-drawer__amount"
                        :class="columnStateClass(column)"
                        type="button"
                        @click="applyColumns(column)"
                    >
                        {{ column }}
                    </button>
                </div>

                <div class="layout-drawer__column">
                    <h4 class="layout-drawer__label">
                        Device
                    </h4>

                    <ul class="layout-drawer__devices">
                        <li
                            v-for="screenObj in screens"
                            :key="screenObj.name"
                            class="layout-drawer__device"
                            :class="{ [STATE_CLASSES.SELECTED]: screenObj.name === selectedScreen.name }"
                        >
                            <button class="layout-drawer__device-button" @click="switchDevice(screenObj)">
                                <font-awesome-icon class="layout-drawer__device-icon" :icon="['fal', screenObj.icon]" />
                            </button>

                            <span v-if="screenObj.name === selectedScreen.name" class="layout-drawer__device-label">
                                {{ screenRange }}
                            </span>
                        </li>
                    </ul>
                </div>

                <div class="layout-drawer__column">
                    <h4 class="layout-drawer__label">
                        Column Gutters
                    </h4>

                    <div class="layout-drawer__slider">
                        <slider
                            :value="gutterSliderIndex"
                            :values="gutterValues"
                            @input="onGutterInput"
                        />
                    </div>
                </div>

                <div class="layout-drawer__column">
                    <h4 class="layout-drawer__label">
                        Direction
                    </h4>

                    <div class="layout-drawer__toggle">
                        <toggle
                            :value="isNormalDirection"
                            :messages="directionLabels"
                            @input="onDirectionInput"
                        />
                    </div>
                </div>
            </section>

            <section class="layout-drawer__row">
                <div class="layout-drawer__column">
                    <h4 class="layout-drawer__label">
                        Column Proportions
                    </h4>

                    <button
                        v-for="option in layoutOptions"
                        :key="option.join('_')"
                        class="layout-drawer__size"
                        :class="layoutStateClass(option)"
                        type="button"
                        @click="selectLayout(option)"
                    >
                        <icon-columns :layout="option" />
                    </button>
                </div>
            </section>

            <footer class="layout-drawer__footer">
                <button class="layout-drawer__apply" @click="applyLayout">
                    APPLY
                </button>
            </footer>
        </div>
    </drawer>
</template>

<script>
import { mapState } from 'vuex';
import { getDevices } from '@/utils/devices';
import Drawer from '@/components/shared/drawer/drawer';
import IconColumns from '@/components/utilities/icon-columns';
import columnLayouts from '@/data/column-layouts';
import { tailwindConfig } from '@/data/tailwind';
import { getNumberFromCssValue } from '@/utils/string';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    components: { Drawer, IconColumns },

    data() {
        return {
            isNormalDirection: true,

            directionLabels: {
                checked: 'Normal',
                unchecked: 'Reversed',
            },

            screens: getDevices(),
            selectedScreen: getDevices()[0],
            selectedWidths: null,
            selectedGutters: null,
            selectedId: null,
            selectedColumns: 1,
            gutterSliderIndex: 0,
            STATE_CLASSES,

            // cap the max gutter to 64px (4.125)
            gutterValues: Object.values(tailwindConfig.theme.padding)
                .filter(v => v.includes('rem') && getNumberFromCssValue(v) < 4.125)
                .sort((a, b) => getNumberFromCssValue(a) - getNumberFromCssValue(b)),
        };
    },

    computed: {
        ...mapState({
            drawer: state => state.page.drawer,
        }),

        columnList() {
            return Object.keys(columnLayouts).map(k => Number(k));
        },

        defaultWidths() {
            return {
                all: columnLayouts[this.selectedColumns][0],
                lap: columnLayouts[this.selectedColumns][0],
                tab: columnLayouts[this.selectedColumns][0],
                mob: columnLayouts[this.selectedColumns][0],
            };
        },

        defaultGutters() {
            // in rems
            return {
                all: 0,
                lap: 0,
                tab: 0,
                mob: 0,
            };
        },

        layoutOptions() {
            return columnLayouts[this.selectedColumns];
        },

        screenRange() {
            const { min, max } = this.selectedScreen;

            switch (true) {
                case !!min && !max:
                    return `${ min } or more`;

                case !!min && !!max:
                    return `${ max } - ${ min }`;

                case !min && !!max:
                    return `${ max } or less`;

                default:
                    return '';
            }
        },
    },

    mounted() {
        this.setDefaults();
    },

    methods: {
        columnStateClass(num) {
            return num === this.selectedColumns ? [STATE_CLASSES.SELECTED] : '';
        },

        layoutStateClass(layout) {
            if (!this.selectedWidths) return '';

            const screen = this.selectedScreen.name;

            return layout.join() === this.selectedWidths[screen].join() ? [STATE_CLASSES.SELECTED] : '';
        },

        applyColumns(num) {
            this.selectedScreen = this.screens[0];
            this.selectedColumns = num;
            this.selectedWidths = this.defaultWidths;
            this.selectedGutters = this.defaultGutters;
        },

        selectLayout(layout) {
            this.selectedWidths[this.selectedScreen.name] = layout;
        },

        applyLayout() {
            const saveData = {
                id: this.selectedId,
                widths: this.selectedWidths,
                gutters: this.selectedGutters,
                flexDirection: this.isNormalDirection ? 'row' : 'reverse',
            };

            this.$root.$emit('flex::layout-drawer::save', saveData);
        },

        setDefaults() {
            const { id, defaultWidths, defaultGutters, defaultFlexDirection } = this.drawer.data;

            this.selectedWidths = defaultWidths;
            this.selectedGutters = defaultGutters;

            this.gutterSliderIndex = this.gutterValues.findIndex(sliderValue => getNumberFromCssValue(sliderValue) === this.selectedGutters.all);
            this.isNormalDirection = defaultFlexDirection === 'row';

            if (defaultWidths && defaultWidths.all) {
                this.selectedColumns = defaultWidths.all.length;
            }

            this.selectedId = id;
        },

        switchDevice(screenObj) {
            this.selectedScreen = screenObj;

            this.gutterSliderIndex = this.gutterValues
                .findIndex(sliderValue => getNumberFromCssValue(sliderValue) === this.selectedGutters[this.selectedScreen.name]);
        },

        onGutterInput(newIndex) {
            const gutterRems = getNumberFromCssValue(this.gutterValues[newIndex]);

            this.selectedGutters[this.selectedScreen.name] = gutterRems;
            this.gutterSliderIndex = newIndex;
        },

        onDirectionInput(isNormalDirection) {
            this.isNormalDirection = isNormalDirection;
        },
    },
};
</script>

<style lang="scss">
@import '@/styles/_includes.scss';

.layout-drawer {
    padding: 40px;
    min-height: 650px;

    &__row {
        display: flex;

        &:not(:first-of-type) {
            margin-top: 50px;
        }
    }

    &__column {
        &:not(:first-of-type) {
            margin-left: 100px;
        }
    }

    &__label {
        font-weight: 700;
        margin-bottom: 15px;
    }

    &__devices {
        display: flex;
    }

    &__device {
        position: relative;

        &:not(:first-of-type) {
            margin-left: 15px;
        }
    }

    &__device-button {
        border: 2px solid transparent;
        padding: 10px;

        .#{ $is-selected } & {
            border-color: var(--form-controlColor);
        }
    }

    &__device-label {
        display: block;
        font-size: 10px;
        position: absolute;
        top: 100%;
        margin-top: 7px;
        white-space: nowrap;
        left: 50%;
        transform: translateX(-50%);
    }

    &__slider {
        width: 300px;
        margin-top: 28px;
    }

    &__toggle {
        margin-top: 28px;
    }

    &__amount,
    &__size {
        padding: 10px;
        margin-right: 5px;
        border: 2px solid transparent;
        vertical-align: top;

        &:hover {
            cursor: pointer;
        }

        &.#{ $is-selected } {
            border-color: var(--form-controlColor);
        }
    }

    &__size {
        width: 300px;
    }

    &__footer {
        margin: 50px 0 30px;
    }

    &__apply {
        color: var(--form-buttonColor);
        background-color: var(--form-buttonBackgroundColor);
        padding: 10px 30px;
        border-radius: 4px;
        font-weight: 700;
        font-size: 14px;

        &:hover {
            color: var(--form-buttonHoveColor);
            background-color: var(--form-buttonHoverBackgroundColor);
        }
    }
}
</style>
